import { addToCartEvent } from "@/analytics/event-logger";
import { FormStateContext } from "@/components/bli-medlem/form-state-context";
import { HorizontalContainer } from "@/components/layout/horizontal-container";
import { HorizontalSpacer } from "@/components/layout/horizontal-spacer";
import { VerticalContainer } from "@/components/layout/vertical-container";
import { DynamicZone } from "@/components/ui/dynamic-zone";
import { LinkButton } from "@/components/ui/link-button";
import { RegisterButton } from "@/components/ui/register-button";
import { Text } from "@/components/ui/text";
import {
  IntervalPaymentType,
  type SubscriptionFragment,
  useGetSubscriptionsLazyQuery,
  useGetSubscriptionsSuspenseQuery,
} from "@/generated/client.generated";
import { UsePage } from "@/hooks/use-page";
import { paths } from "@/utils/paths-utils";
import { stringToNumberOrUndefined } from "@/utils/string-utils";
import { usePathname, useSearchParams } from "next/navigation";
import { parseAsString, useQueryState } from "nuqs";
import { equals } from "rambda";
import { useContext, useEffect, useState } from "react";
import { sendStepEvent } from "../analytics";
import { UserJourney } from "../types";
import { sortSubscriptionList, userJourneyToRecurrenceString } from "../utils";
import { DiscountCodeForm } from "./discount-code-form";

export function AccountTypeForm() {
  const page = UsePage("/bli-medlem");
  const pathName = usePathname();
  const searchParams = useSearchParams();

  const { formData, currentStepIndex, step, next } = useContext(FormStateContext);

  const [discountCode, setDiscountCode] = useQueryState("discountCode", parseAsString);

  const accountTypeOptionsInitial = sortSubscriptionList(
    useGetSubscriptionsSuspenseQuery({
      variables: {
        centerId: `${formData.mainCenter?.id}`,
        code: formData.accountType?.discountCode?.code ?? "",
        customerTypeId: `${formData.customerType?.id}`,
        recurrence: `${userJourneyToRecurrenceString(formData.userJourney)}`,
        organization: formData.organization?.id ?? "",
      },
    }).data?.Subscriptions ?? [],
  );

  const [accountTypeOptions, setAccountTypeOptions] =
    useState<ReadonlyArray<SubscriptionFragment>>(accountTypeOptionsInitial);

  const [activeDiscountCode, setActiveDiscountCode] = useState<string | undefined>(undefined);

  const [getAccountTypeOptions] = useGetSubscriptionsLazyQuery();

  const [discountCodeErrorMessage, setDiscountCodeErrorMessage] = useState("");

  function handleSetDiscountCode(code: string | null) {
    //reset discountCode
    if (!code) {
      setActiveDiscountCode(undefined);
      setAccountTypeOptions(accountTypeOptionsInitial);
      setDiscountCode(null);
      return;
    }
    const upperCaseCode = code.toLocaleUpperCase();
    //if same as before return
    if (equals(upperCaseCode, activeDiscountCode?.toLocaleUpperCase())) {
      setAccountTypeOptions(accountTypeOptionsInitial);
      return;
    }

    // spørring med kode
    getAccountTypeOptions({
      variables: {
        centerId: `${formData.mainCenter?.id}`,
        code: upperCaseCode,
        customerTypeId: `${formData.customerType?.id}`,
        recurrence: `${userJourneyToRecurrenceString(formData.userJourney)}`,
        organization: "",
      },
    }).then((response) => {
      // hvis kode er gyldig: oppdater listen og setter aktiv rabattkode
      if (response.data?.Subscriptions?.find((sub) => sub.discountCode)) {
        setAccountTypeOptions(sortSubscriptionList(response.data?.Subscriptions || []));
        setDiscountCodeErrorMessage("");
        setActiveDiscountCode(code);
        setDiscountCode(code);
      }
      // hvis kode er ugyldig: bruk initial data og reset aktiv rabattkode
      else {
        setAccountTypeOptions(accountTypeOptionsInitial);
        setDiscountCodeErrorMessage("Ugyldig kampanjekode");
        setActiveDiscountCode("");
        setDiscountCode(null);
      }
    });
  }

  // Kjør for rabattkode fra query parameter ved første render
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    //avoid infinite loop from useEffect
    discountCode && discountCode !== activeDiscountCode && handleSetDiscountCode(discountCode);
  }, [discountCode]);

  if (formData.userJourney === UserJourney.Organization && accountTypeOptions.length === 0) {
    return (
      <VerticalContainer horizontalPlacement="center">
        <HorizontalSpacer />
        <Text value="Vi fant dessverre ingen bedriftsavtale tilknyttet din bedrift." align="center" />
        <HorizontalSpacer />
        <LinkButton href={paths.kundeserviceLandingPage} buttonType="primary">
          <Text value="Kontakt kundeservice" />
        </LinkButton>
      </VerticalContainer>
    );
  }
  return (
    <>
      {formData.userJourney !== UserJourney.Organization && (
        <>
          <DiscountCodeForm
            activeDiscountCode={activeDiscountCode}
            handleSetDiscountCode={handleSetDiscountCode}
            errorMessage={discountCodeErrorMessage}
          />
          <HorizontalSpacer />
        </>
      )}

      {step?.showDynamicZone && (
        <VerticalContainer horizontalPlacement="center" width="full">
          <HorizontalSpacer size="x-small" />

          <DynamicZone blocks={page?.blocks ?? []} />
        </VerticalContainer>
      )}

      <HorizontalContainer horizontalPlacement="center">
        {accountTypeOptions.map((at, index) => (
          <RegisterButton
            key={at.id}
            ingress={at.ingress ?? undefined}
            isCampaign={at.isCampaign}
            length={accountTypeOptions.length}
            notification={at.notification ?? undefined}
            onClick={() => {
              //only for estimating total value of membership
              const totalValue = Math.round(
                (formData.userJourney !== UserJourney.Single ? at.price.amount * 12 : at.price.amount) / 100,
              );
              //analytics
              sendStepEvent(at.name, currentStepIndex + 1, step?.shortTitle, formData.userJourney);
              addToCartEvent(totalValue, [
                {
                  alder: formData.customerType?.title,
                  index: index,
                  isRecurring: formData.userJourney !== UserJourney.Single,
                  item_brand: "3T",
                  item_id: at.id,
                  item_name: at.name,
                  location_id: formData?.mainCenter?.page.title,
                  medlemskap: at.name,
                  price: totalValue,
                  quantity: 1,
                  senter: formData?.mainCenter?.page.title,
                },
              ]);

              if (
                `${pathName}?${searchParams.toString()}` ===
                paths.bliMedlemPrefilled(
                  formData.userJourney,
                  formData.customerType?.id,
                  formData.mainCenter?.id,
                  at.id,
                  activeDiscountCode,
                  formData.contactInfo.email,
                  undefined,
                  formData.organization?.id ?? undefined,
                )
              ) {
                next();
              }
            }}
            url={paths.bliMedlemPrefilled(
              formData.userJourney,
              formData.customerType?.id,
              formData.mainCenter?.id,
              at.id,
              activeDiscountCode,
              undefined,
              undefined,
              formData.organization?.id ?? undefined,
            )}
            priceAmount={at.discount?.amount ?? at.price.amount}
            priceInterval={at.intervalPayment === IntervalPaymentType.Month ? "1, MONTH" : ""}
            selected={stringToNumberOrUndefined(at.id) === formData.accountType?.id}
            sellingPoints={at.membershipSummary ?? undefined}
            sellingPointsHidden={at.membershipSummaryHidden ?? undefined}
            title={at.name}
          />
        ))}
      </HorizontalContainer>
    </>
  );
}
